/**
 * "Pairs well with" in the cart drawer (snippets/cart-drawer.liquid → sections/cart-recommendations.liquid).
 * Loads complementary products for the newest cart line (falling back to related products) through the
 * Product Recommendations API, and adds an item with its "+" button via /cart/add, then re-renders the
 * drawer through <cart-drawer>.renderContents() like the product form does.
 * Results are cached per cart contents so the drawer's re-renders (quantity changes) don't flash.
 */
if (!customElements.get('cart-recommendations')) {
  customElements.define(
    'cart-recommendations',
    class CartRecommendations extends HTMLElement {
      static cache = new Map();

      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        if (!this.dataset.url) return;

        const cached = CartRecommendations.cache.get(this.dataset.cacheKey);
        if (cached !== undefined) {
          this.render(cached);
          return;
        }

        // The drawer is in every page but off-screen until opened: only fetch once it is on screen.
        this.observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.load();
        });
        this.observer.observe(this.closest('.drawer__inner') || this);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.observer?.disconnect();
        this.controller?.abort();
      }

      async load() {
        this.controller?.abort();
        this.controller = new AbortController();
        try {
          let html = await this.fetchRecommendations('complementary');
          if (!html) html = await this.fetchRecommendations('related');
          CartRecommendations.cache.set(this.dataset.cacheKey, html);
          this.render(html);
        } catch (error) {
          if (error.name !== 'AbortError') console.error(error);
        }
      }

      async fetchRecommendations(intent) {
        const response = await fetch(`${this.dataset.url}&intent=${intent}`, { signal: this.controller.signal });
        if (!response.ok) return '';
        const text = await response.text();
        const source = new DOMParser().parseFromString(text, 'text/html').querySelector('cart-recommendations');
        return source && source.querySelector('[data-cart-recs-ready]') ? source.innerHTML.trim() : '';
      }

      render(html) {
        if (!html) {
          this.hidden = true;
          this.innerHTML = '';
          return;
        }
        this.innerHTML = html;
        this.hidden = false;
      }

      onClick(event) {
        const button = event.target.closest('[data-variant-id]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.addToCart(button);
      }

      showError(message) {
        const error = this.querySelector('.ob-cart-recs__error');
        if (!error) return;
        error.textContent = message || '';
        error.hidden = !message;
      }

      async addToCart(button) {
        if (button.getAttribute('aria-disabled') === 'true') return;

        const drawer = document.querySelector('cart-drawer');
        const variantId = button.dataset.variantId;
        this.showError();
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        if (drawer) {
          formData.append(
            'sections',
            drawer.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        try {
          const response = await fetch(`${routes.cart_add_url}`, config);
          const state = await response.json();

          if (state.status) {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'cart-recommendations',
              productVariantId: variantId,
              errors: state.errors || state.description,
              message: state.message,
            });
            this.showError(state.description || state.message || window.cartStrings?.error);
            return;
          }

          if (!drawer) {
            window.location = window.routes.cart_url;
            return;
          }

          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-recommendations',
            productVariantId: variantId,
            cartData: state,
          });
          drawer.renderContents(state);
        } catch (error) {
          console.error(error);
          this.showError(window.cartStrings?.error);
        } finally {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        }
      }
    }
  );
}
